import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box, Button, Checkbox, IconButton, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import CollectionsIcon from '@mui/icons-material/Collections';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublishIcon from '@mui/icons-material/Publish';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { createGiftCard } from '../../api';
import { Notifications, Stories } from '../../store/slices';
import { AppDialog, StatusChip } from '../ui';
import { STORY_CDN, STORY_RENDITIONS, bunnyDashboardUrl, storyStatus, storyVideoId } from '../../lib/storyOptions';
import { formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

type SelectedItem = { storyId: any; authorId: any };

/** Thumbnail that opens an inline, auto-playing video popover. */
const VideoPreview = ({ videoLink, imageLink, label }: { videoLink: string; imageLink?: string; label: string }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (open && videoRef.current) {
            videoRef.current.play().catch(() => undefined);
        }
    }, [open]);

    const videoId = storyVideoId(videoLink);

    return (
        <>
            <button type="button" className={classes.thumbButton} onClick={(event) => setAnchorEl(event.currentTarget)} aria-label={`Play ${label}`}>
                {imageLink ? <img src={imageLink} className={classes.thumb} alt="" loading="lazy" /> : <div className={classes.thumb} />}
                <span className={classes.thumbOverlay} aria-hidden="true"><PlayCircleOutlineIcon /></span>
            </button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                transformOrigin={{ vertical: 'center', horizontal: 'center' }}
                PaperProps={{ sx: { p: 1, overflow: 'visible' } }}
            >
                <Box sx={{ position: 'relative', width: 600, maxWidth: '90vw' }}>
                    <IconButton
                        onClick={() => setAnchorEl(null)}
                        aria-label="Close video"
                        size="small"
                        sx={{ position: 'absolute', top: -18, right: -18, bgcolor: 'background.paper', boxShadow: 2, zIndex: 1, '&:hover': { bgcolor: 'background.paper' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <video ref={videoRef} controls style={{ display: 'block', width: '100%', height: 500, objectFit: 'contain', background: '#000', borderRadius: 8 }}>
                        {STORY_RENDITIONS.map((resolution) => (
                            <source key={resolution} src={`${STORY_CDN}/${videoId}/play_${resolution}p.mp4#t=0.001`} type="video/mp4" />
                        ))}
                    </video>
                </Box>
            </Popover>
        </>
    );
};

interface StoriesTableProps {
    stories: any[];
    paging: { page: number; perPage: number; total: number; [key: string]: any };
    pagingUpdate: (params: any) => void;
}

/** Stories table with media previews, bulk publish and bulk gift cards (one request per story, 400 ms apart). */
export default function StoriesTable({ stories, paging, pagingUpdate }: StoriesTableProps) {
    const classes = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [pending, setPending] = useState(false);
    const [giftInputValue, setGiftInputValue] = useState<number>(0);
    const [isPopUp, setIsPopUp] = useState(false);
    const [isPublishPopUp, setIsPublishPopUp] = useState(false);
    const [selectedId, setSelectedId] = useState<SelectedItem[]>([]);

    // Image viewer state
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState<string[]>([]);
    const [viewerIndex, setViewerIndex] = useState(0);

    const openImageViewer = (images: string[], index: number) => {
        setViewerImages(images);
        setViewerIndex(index);
        setIsImageViewerOpen(true);
    };
    const nextImage = () => setViewerIndex((prev) => (prev + 1) % viewerImages.length);
    const prevImage = () => setViewerIndex((prev) => (prev - 1 + viewerImages.length) % viewerImages.length);

    const downloadImage = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `story-image-${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed', error);
            window.open(url, '_blank');
        }
    };

    const handleCheckBoxChange = (checked: boolean, storyId: any, authorId: any) => {
        if (checked) {
            setSelectedId([...selectedId, { storyId, authorId }]);
        } else {
            setSelectedId(selectedId.filter((item) => item.storyId !== storyId));
        }
    };

    const allOnPageSelected = stories.length > 0 && stories.every((story) => selectedId.some((item) => item.storyId === story.storyId));
    const toggleAll = (checked: boolean) => {
        if (checked) {
            const missing = stories.filter((story) => !selectedId.some((item) => item.storyId === story.storyId)).map((story) => ({ storyId: story.storyId, authorId: story.authorId }));
            setSelectedId([...selectedId, ...missing]);
        } else {
            setSelectedId(selectedId.filter((item) => !stories.some((story) => story.storyId === item.storyId)));
        }
    };

    const createGiftCart = async (data: SelectedItem, inputValue: number) => {
        try {
            await createGiftCard({
                data: { gfStoryId: data.storyId, gfAuthorId: data.authorId, amount: inputValue },
                onSuccess: async () => {
                    dispatch(Notifications.setNotifications({ text: 'Gift card created', type: 'success' }));
                },
            });
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: `Something went wrong ID:${data.storyId}`, type: 'error' }));
        }
    };

    const processGiftCards = (items: SelectedItem[], inputValue: number, index = 0) => {
        if (index >= items.length) {
            setGiftInputValue(0);
            setPending(false);
            return;
        }
        createGiftCart(items[index], inputValue);
        setTimeout(() => processGiftCards(items, inputValue, index + 1), 400);
    };

    const publishStory = async (data: SelectedItem) => {
        try {
            dispatch<any>(Stories.updateStory({ id: data.storyId, formData: { isPublished: 1 } })).then(() => {});
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: `Something went wrong ID:${data.storyId}`, type: 'error' }));
        }
    };

    const processPublish = (items: SelectedItem[], index = 0) => {
        if (index >= items.length) {
            setGiftInputValue(0);
            setPending(false);
            return;
        }
        publishStory(items[index]);
        setTimeout(() => processPublish(items, index + 1), 400);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1, perPage: paging.perPage });
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, perPage: +event.target.value });
    };

    return (
        <>
            <AppDialog
                open={isPopUp}
                onClose={() => { setIsPopUp(false); setGiftInputValue(0); }}
                title={`Create gift cards for ${selectedId.length} ${selectedId.length === 1 ? 'story' : 'stories'}`}
                description="One gift card of this amount is issued to the author of every selected story."
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => { setIsPopUp(false); setGiftInputValue(0); }}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={giftInputValue === 0}
                            onClick={() => { setIsPopUp(false); setPending(true); processGiftCards(selectedId, giftInputValue); }}
                        >
                            Create gift cards
                        </Button>
                    </>
                )}
            >
                <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    fullWidth
                    autoFocus
                    inputProps={{ inputMode: 'numeric', min: 1 }}
                    onChange={(event) => setGiftInputValue(Number(event.target.value))}
                />
            </AppDialog>

            <AppDialog
                open={isPublishPopUp}
                onClose={() => setIsPublishPopUp(false)}
                title={`Publish ${selectedId.length} ${selectedId.length === 1 ? 'story' : 'stories'}?`}
                description="Every selected story gets a public page. There is no bulk unpublish; use the story page to hide one again."
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => setIsPublishPopUp(false)}>Cancel</Button>
                        <Button variant="contained" onClick={() => { setIsPublishPopUp(false); setPending(true); processPublish(selectedId); }}>
                            Publish
                        </Button>
                    </>
                )}
            />

            {isImageViewerOpen && (
                <div className={classes.lightbox} role="dialog" aria-label="Story images">
                    <IconButton onClick={() => setIsImageViewerOpen(false)} className={classes.lightboxClose} aria-label="Close">
                        <CloseIcon fontSize="large" />
                    </IconButton>
                    {viewerImages.length > 1 && (
                        <IconButton onClick={prevImage} className={classes.lightboxNav} aria-label="Previous image"><ArrowBackIcon /></IconButton>
                    )}
                    <div>
                        <img src={viewerImages[viewerIndex]} alt="" className={classes.lightboxImage} />
                        <div className={classes.lightboxBar}>
                            <span>{viewerIndex + 1} / {viewerImages.length}</span>
                            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={() => downloadImage(viewerImages[viewerIndex])}>
                                Download
                            </Button>
                        </div>
                    </div>
                    {viewerImages.length > 1 && (
                        <IconButton onClick={nextImage} className={classes.lightboxNav} aria-label="Next image"><ArrowForwardIcon /></IconButton>
                    )}
                </div>
            )}

            {selectedId.length > 0 && (
                <div className={classes.toolbar} role="toolbar" aria-label="Bulk actions">
                    <span>{selectedId.length} selected</span>
                    <Button variant="text" size="small" onClick={() => setSelectedId([])}>Clear selection</Button>
                    <div className={classes.toolbarActions}>
                        <Button variant="outlined" size="small" startIcon={<CardGiftcardIcon />} onClick={() => setIsPopUp(true)} disabled={pending}>
                            Gift cards
                        </Button>
                        <Button variant="contained" size="small" startIcon={<PublishIcon />} onClick={() => setIsPublishPopUp(true)} disabled={pending}>
                            Publish
                        </Button>
                    </div>
                </div>
            )}

            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="Stories" aria-busy={pending}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allOnPageSelected}
                                    indeterminate={!allOnPageSelected && selectedId.length > 0}
                                    onChange={(event) => toggleAll(event.target.checked)}
                                    inputProps={{ 'aria-label': 'Select all stories on this page' }}
                                />
                            </TableCell>
                            <TableCell>Story</TableCell>
                            <TableCell>Image</TableCell>
                            <TableCell>Gallery</TableCell>
                            <TableCell>Video</TableCell>
                            <TableCell>Secondary</TableCell>
                            <TableCell>Production</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Author</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Published</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stories.map((story) => {
                            const status = storyStatus(story.statusId);
                            const dashboard = bunnyDashboardUrl(story.videoLink);
                            return (
                                <TableRow key={story.storyId} hover selected={selectedId.some((item) => item.storyId === story.storyId)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={selectedId.some((item) => item.storyId === story.storyId)}
                                            onChange={(event) => handleCheckBoxChange(event.target.checked, story.storyId, story.authorId)}
                                            inputProps={{ 'aria-label': `Select story ${story.storyId}` }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/stories/${story.storyId}/details`} className={classes.mono} style={{ fontWeight: 600, color: 'inherit', textDecoration: 'none' }}>
                                            #{story.storyId}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {story.imageLink ? (
                                            <Tooltip title={<img className={classes.preview} src={story.imageLink} alt="" />} enterTouchDelay={200} slotProps={{ tooltip: { sx: { maxWidth: 'none', p: 0.5, bgcolor: 'transparent' } } }}>
                                                <img className={classes.thumb} src={story.imageLink} alt="" loading="lazy" />
                                            </Tooltip>
                                        ) : <div className={classes.thumb} />}
                                    </TableCell>
                                    <TableCell>
                                        {story.storyImages && story.storyImages.length > 0 ? (
                                            <button type="button" className={classes.thumbButton} onClick={() => openImageViewer(story.storyImages || [], 0)} aria-label={`Open ${story.storyImages.length} story images`}>
                                                <img src={story.storyImages[0]} className={classes.thumb} alt="" loading="lazy" />
                                                {story.storyImages.length > 1 && (
                                                    <span className={classes.thumbBadge}><CollectionsIcon />{story.storyImages.length}</span>
                                                )}
                                            </button>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{story.videoLink ? <VideoPreview videoLink={story.videoLink} imageLink={story.imageLink} label="main video" /> : null}</TableCell>
                                    <TableCell>{story.videoLinkSecondary ? <VideoPreview videoLink={story.videoLinkSecondary} imageLink={story.imageLinkSecondary} label="secondary video" /> : null}</TableCell>
                                    <TableCell>{story.videoLinkProduction ? <VideoPreview videoLink={story.videoLinkProduction} imageLink={story.imageLinkProduction} label="production video" /> : null}</TableCell>
                                    <TableCell className={classes.nowrap}>{formatDate(story.date)}</TableCell>
                                    <TableCell>{story.firstName || '—'}</TableCell>
                                    <TableCell><StatusChip size="sm" tone={status.tone}>{status.label}</StatusChip></TableCell>
                                    <TableCell>
                                        <StatusChip size="sm" dot tone={story.isPublished ? 'success' : 'neutral'}>{story.isPublished ? 'Published' : 'Hidden'}</StatusChip>
                                    </TableCell>
                                    <TableCell>
                                        <div className={classes.actions}>
                                            {dashboard && (
                                                <Tooltip title="Open in the Bunny dashboard">
                                                    <IconButton size="small" component="a" href={dashboard} target="_blank" rel="noreferrer" aria-label="Open video in Bunny">
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`/stories/${story.storyId}/details`)}>
                                                Edit
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 15, 25]}
                component="div"
                count={paging.total}
                rowsPerPage={paging.perPage}
                page={Math.max(0, paging.page - 1)}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>
    );
}
